import * as React from "react";
import { RouteComponentProps } from "@reach/router";
import { FormikProps, Field } from "formik";
import { cx, css } from "@emotion/css";
import { navigate } from "../support/hashRouting";
import { FormValues } from "../support/FormValues";
import bootstrapStyles from "../css/bootstrap.module.css";
import formGroupStyles from "../css/formgroup.module.css";
import Breadcrumbs from "../components/Breadcrumbs";
import { ClassName } from "../support/className";
import { StepHeader } from "../components/StepHeader";
import { GeneralButton } from "../components/GeneralButton";
import { FieldNameEnum } from "../support/FieldNameEnum";
import FieldText from "../components/FieldText";
import { FieldCustomMask } from "../components/FieldCustomMask";
import { useScrollTop } from "../customHooks/scrollTop";
import {
  fundEnumList,
  fundCodeEnumList,
  fundPercentEnumList
} from "../support/BYOFundEnums";

interface PersonalInformationProps extends RouteComponentProps {
  formikBag: FormikProps<FormValues>;
}

const stepHeaderText = "Great! Now let's talk about you.";
const stepNoteText = "All fields required unless otherwise noted.";

const PersonalInformation = ({
  formikBag: { values, errors, setFieldValue, setFieldTouched },
  location
}: PersonalInformationProps) => {
  useScrollTop(location);

  const potentialErrorFields = [
    FieldNameEnum.firstName,
    FieldNameEnum.lastName,
    FieldNameEnum.birthDate,
    FieldNameEnum.ssn,
    FieldNameEnum.email,
    FieldNameEnum.primaryPhone,
    FieldNameEnum.secondaryPhone
  ];

  // Checks all errors in the entire application and compares them with the fields on the current page
  const pageValid = potentialErrorFields.every(field => !(field in errors));

  return (
    <div className={ClassName.personalInformation}>
      <Breadcrumbs currentStep={2} />
      <div className={bootstrapStyles.row}>
        <div
          className={cx(
            bootstrapStyles["col-md-12"],
            formGroupStyles.contentPadding
          )}
        >
          <StepHeader stepHeader={stepHeaderText} stepNote={stepNoteText} />
        </div>
      </div>
      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.firstName}
            label="First Name"
            component={FieldText}
          />
        </div>
      </div>

      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.lastName}
            label="Last Name"
            component={FieldText}
          />
        </div>
      </div>

      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-xs-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.birthDate}
            label="Date of Birth"
            component={FieldCustomMask}
            onChange={() => {
              if (!values.byo) {
                setFieldValue(fundEnumList[0], "");
                setFieldValue(fundCodeEnumList[0], "");
                setFieldValue(fundPercentEnumList[0], "");
                setFieldTouched(fundEnumList[0], false);
                setFieldTouched(fundPercentEnumList[0], false);
              }
            }}
            mask="date"
            guide
          />
        </div>
        <div className={bootstrapStyles["col-xs-6"]}>
          <div
            className={cx(
              formGroupStyles["form-group"],
              css`
                color: grey;
                font-style: italic;
                margin-top: 40px !important;
              `
            )}
          >
            <span
              className={css`
                font-size: 14px !important;
              `}
            >
              Format MM/DD/YYYY
            </span>
          </div>
        </div>
      </div>

      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-xs-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.ssn}
            label="Social Security Number"
            component={FieldCustomMask}
            mask="ssn"
            guide
          />
        </div>
      </div>

      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.email}
            label="Email Address"
            component={FieldText}
          />
        </div>
      </div>

      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.primaryPhone}
            label="Primary Phone Number"
            component={FieldCustomMask}
            mask="phone"
            guide
          />
        </div>
      </div>

      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-6"]}>
          <Field
            type="text"
            spellCheck="false"
            name={FieldNameEnum.secondaryPhone}
            label="Secondary Phone Number (optional)"
            component={FieldCustomMask}
            mask="phone"
            guide
          />
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div className={bootstrapStyles["col-md-12"]}>
          <hr />
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div className={cx(bootstrapStyles["col-md-12"])}>
          <GeneralButton
            text="back"
            id="back"
            buttonType="secondary"
            onClick={() => {
              navigate("/transferInstructions");
            }}
          />
          <div
            className={css`
              float: right;
            `}
          >
            <GeneralButton
              text="continue"
              id="continue"
              onClick={() => {
                navigate("/fundSelection");
              }}
              buttonType="primary"
              disabled={!pageValid}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInformation;
